<script>
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import ImapSettings from '../ImapSettings.vue';
import SmtpSettings from '../SmtpSettings.vue';
import PhoneSettings from '../PhoneSettings.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import HtmlSignaturePreview from '../components/HtmlSignaturePreview.vue';
import HmacSecretKey from '../components/HmacSecretKey.vue';
import EmailDnsCheck from '../components/EmailDnsCheck.vue';
import WhatsappConnectionInfo from '../channels/whatsapp/ConnectionInfo.vue';
import { emailAliasesAttributes } from '../helpers/emailAliases';

export default {
  components: {
    EmailDnsCheck,
    SettingsSection,
    ImapSettings,
    SmtpSettings,
    NextButton,
    PhoneSettings,
    HtmlSignaturePreview,
    HmacSecretKey,
    WhatsappConnectionInfo,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      hmacMandatory: false,
      commentsEnabled: false,
      customEmailDomainEnabled: false,
      emailFetchingEnabled: false,
      kauflandClientKey: '',
      kauflandSecretKey: '',
      overrideEmailSender: '',
      // The alias rows being edited, { id, email }; a new row has no id yet.
      emailAliases: [],
      // Index into `additionalSignatures` per alias row, or -1 for
      // "Primary (default)" (falls back to the inbox html_signature).
      alternativeEmailSignatures: [],
      // Pool of reusable signatures: [{ html }]. Persisted as
      // additional_html_signatures = [{ html, alternative_emails: [] }] on save.
      additionalSignatures: [],
    };
  },
  computed: {
    isOAuthProvider() {
      return !!this.inbox.provider;
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.hmacMandatory = this.inbox.hmac_mandatory || false;
      this.commentsEnabled = this.inbox.comments_enabled || false;
      this.customEmailDomainEnabled = this.inbox.custom_domain_enabled || false;
      this.emailFetchingEnabled = this.inbox.email_fetching_enabled || false;
      this.overrideEmailSender = this.inbox.override_email_sender || '';
      this.kauflandClientKey = this.inbox.client_key || '';
      this.kauflandSecretKey = '';
      this.emailAliases = (this.inbox.email_aliases || []).map(
        ({ id, email }) => ({ id, email })
      );
      this.hydrateAdditionalSignatures();
    },
    hydrateAdditionalSignatures() {
      const stored = Array.isArray(this.inbox.additional_html_signatures)
        ? this.inbox.additional_html_signatures
        : [];
      // Each stored signature is a first-class entry that carries the aliases
      // assigned to it, so it persists even with no aliases attached.
      this.additionalSignatures = stored.map(entry => ({
        html: entry?.html ?? '',
      }));
      const emailToIndex = new Map();
      stored.forEach((entry, index) => {
        const emails = Array.isArray(entry?.alternative_emails)
          ? entry.alternative_emails
          : [];
        emails.forEach(email => {
          if (email) emailToIndex.set(email.trim().toLowerCase(), index);
        });
      });
      this.alternativeEmailSignatures = this.emailAliases.map(({ email }) => {
        const index = emailToIndex.get((email || '').trim().toLowerCase());
        return index == null ? -1 : index;
      });
    },
    addAlternativeEmail() {
      this.emailAliases.push({ email: '' });
      this.alternativeEmailSignatures.push(-1);
    },
    removeAlternativeEmail(index) {
      this.emailAliases.splice(index, 1);
      this.alternativeEmailSignatures.splice(index, 1);
    },
    addSignature() {
      this.additionalSignatures.push({ html: '' });
    },
    removeSignature(index) {
      this.additionalSignatures.splice(index, 1);
      // Keep the per-email selection pointing at the correct pool entry.
      this.alternativeEmailSignatures = this.alternativeEmailSignatures.map(
        selected => {
          if (selected === index) return -1;
          if (selected > index) return selected - 1;
          return selected;
        }
      );
    },
    updateSignatureHtml(index, html) {
      this.additionalSignatures[index] = {
        ...this.additionalSignatures[index],
        html,
      };
    },
    buildAdditionalHtmlSignatures() {
      // Collect the aliases assigned to each signature.
      const emailsBySignature = this.additionalSignatures.map(() => []);
      this.emailAliases.forEach(({ email }, index) => {
        const trimmed = (email || '').trim();
        const selected = this.alternativeEmailSignatures[index];
        if (!trimmed || selected == null || selected < 0) return;
        if (emailsBySignature[selected]) {
          emailsBySignature[selected].push(trimmed);
        }
      });
      // Persist every signature that has html, even when no alias is assigned.
      return this.additionalSignatures
        .map((signature, index) => ({
          html: (signature.html || '').trim(),
          alternative_emails: emailsBySignature[index] || [],
        }))
        .filter(signature => signature.html.length > 0);
    },
    buildChannelPayload() {
      if (this.isAFacebookInbox) {
        return { comments_enabled: this.commentsEnabled };
      }
      if (this.isAWebWidgetInbox || this.isAPIInbox) {
        return { hmac_mandatory: this.hmacMandatory };
      }
      if (this.isASalesforceChannel) {
        return { override_email_sender: this.overrideEmailSender };
      }
      if (this.isAKauflandChannel) {
        return {
          client_key: this.kauflandClientKey.trim(),
          secret_key: this.kauflandSecretKey.trim(),
        };
      }
      if (this.isAnEmailChannel) {
        const channel = {
          email_fetching_enabled: this.emailFetchingEnabled,
          custom_domain_enabled: this.customEmailDomainEnabled,
          email_aliases_attributes: emailAliasesAttributes(
            this.inbox.email_aliases,
            this.emailAliases
          ),
        };
        if (this.customEmailDomainEnabled) {
          channel.imap_enabled = false;
        }
        return channel;
      }
      return {};
    },
    async updateConfiguration() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: this.buildChannelPayload(),
        };
        if (this.isAnEmailChannel) {
          payload.additional_html_signatures =
            this.buildAdditionalHtmlSignatures();
        }
        await this.$store.dispatch('inboxes/updateInbox', payload);

        if (
          this.isAnEmailChannel &&
          this.customEmailDomainEnabled &&
          this.$refs.imapSettings
        ) {
          await this.$refs.imapSettings.updateInbox();
        }

        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div v-if="isATwilioChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsSection>
  </div>
  <div v-else-if="isALineChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsSection>
  </div>
  <div v-else-if="isASalesforceChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.API_CALLBACK.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsSection>
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.BEARER_TOKEN.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.BEARER_TOKEN.SUBTITLE')"
    >
      <woot-code :script="inbox.bearer_token" lang="html" />
    </SettingsSection>
    <SettingsSection
      :title="
        $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.OVERRIDE_EMAIL_SENDER.TITLE')
      "
      :sub-title="
        $t('INBOX_MGMT.ADD.SALESFORCE_CHANNEL.OVERRIDE_EMAIL_SENDER.SUBTITLE')
      "
    >
      <woot-input
        v-model="overrideEmailSender"
        type="text"
        :placeholder="
          $t(
            'INBOX_MGMT.ADD.SALESFORCE_CHANNEL.OVERRIDE_EMAIL_SENDER.PLACEHOLDER'
          )
        "
      />
    </SettingsSection>
    <SettingsSection :show-border="false">
      <NextButton
        type="submit"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        @click="updateConfiguration"
      />
    </SettingsSection>
  </div>
  <div v-else-if="isAKauflandChannel" class="mx-8">
    <SettingsSection
      v-if="inbox.reauthorization_required"
      :title="$t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.DISCONNECTED.TITLE')"
    >
      <p class="text-n-ruby-11">
        {{ $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.DISCONNECTED.MESSAGE') }}
      </p>
    </SettingsSection>
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.CLIENT_KEY.LABEL')"
      :sub-title="$t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.CLIENT_KEY.SUBTITLE')"
    >
      <input
        v-model="kauflandClientKey"
        type="text"
        :placeholder="
          $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.CLIENT_KEY.PLACEHOLDER')
        "
      />
    </SettingsSection>
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.SECRET_KEY.LABEL')"
      :sub-title="$t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.SECRET_KEY.SUBTITLE')"
    >
      <input
        v-model="kauflandSecretKey"
        type="password"
        :placeholder="
          $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.SECRET_KEY.PLACEHOLDER')
        "
      />
    </SettingsSection>
    <SettingsSection :show-border="false">
      <NextButton
        type="submit"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        @click="updateConfiguration"
      />
    </SettingsSection>
  </div>
  <div v-else-if="isAnAllegroChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.ALLEGRO_CHANNEL.SELLER_LOGIN.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.ALLEGRO_CHANNEL.SELLER_LOGIN.SUBTITLE')"
    >
      <woot-code :script="inbox.seller_login" lang="html" />
    </SettingsSection>
  </div>
  <div v-else-if="isAWhatsAppCloudChannel" class="mx-8">
    <WhatsappConnectionInfo :inbox="inbox" />
  </div>
  <div v-else-if="isAFacebookInbox" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.FACEBOOK_COMMENTS_TITLE')"
    >
      <div class="flex items-center gap-2">
        <input id="commentsEnabled" v-model="commentsEnabled" type="checkbox" />
        <label for="commentsEnabled">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_FACEBOOK_COMMENTS.LABEL') }}
        </label>
      </div>
    </SettingsSection>
    <SettingsSection :show-border="false">
      <NextButton
        type="submit"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        @click="updateConfiguration"
      />
    </SettingsSection>
  </div>
  <div v-else-if="isAWebWidgetInbox">
    <div class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_HEADING')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_SUB_HEAD')"
      >
        <woot-code
          :script="inbox.web_widget_script"
          lang="html"
          :codepen-title="`${inbox.name} - Chatwoot Widget Test`"
          enable-code-pen
        />
      </SettingsSection>

      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      >
        <HmacSecretKey :inbox="inbox" />
        <template #subTitle>
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.chatwoot.com/docs/product/channels/live-chat/sdk/identity-validation/"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_LINK_TO_DOCS') }}
          </a>
        </template>
      </SettingsSection>
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
      >
        <div class="flex items-center gap-2">
          <input id="hmacMandatory" v-model="hmacMandatory" type="checkbox" />
          <label for="hmacMandatory">
            {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
          </label>
        </div>
      </SettingsSection>
      <SettingsSection :show-border="false">
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          @click="updateConfiguration"
        />
      </SettingsSection>
    </div>
  </div>
  <div v-else-if="isAPIInbox" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT')"
    >
      <woot-code :script="inbox.inbox_identifier" />
    </SettingsSection>

    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION')"
    >
      <HmacSecretKey :inbox="inbox" />
    </SettingsSection>
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
    >
      <div class="flex items-center gap-2">
        <input id="hmacMandatory" v-model="hmacMandatory" type="checkbox" />
        <label for="hmacMandatory">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
        </label>
      </div>
    </SettingsSection>
    <SettingsSection :show-border="false">
      <NextButton
        type="submit"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        @click="updateConfiguration"
      />
    </SettingsSection>
  </div>
  <div v-else-if="isPhoneInbox" class="mx-8">
    <PhoneSettings :inbox="inbox" />
  </div>
  <div v-else-if="isAnEmailChannel">
    <div class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT')"
      >
        <woot-code :script="inbox.forward_to_email" />
      </SettingsSection>
    </div>
    <div class="mx-8">
      <EmailDnsCheck :inbox="inbox" />
    </div>
    <div class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.EMAIL_FETCHING_ENABLED_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.EMAIL_FETCHING_ENABLED_DESCRIPTION')
        "
      >
        <div class="flex items-center gap-2">
          <input
            id="emailFetchingEnabled"
            v-model="emailFetchingEnabled"
            type="checkbox"
          />
          <label for="emailFetchingEnabled">
            {{ $t('INBOX_MGMT.EDIT.ENABLE_EMAIL_FETCHING.LABEL') }}
          </label>
        </div>
      </SettingsSection>
    </div>
    <div v-if="!isOAuthProvider" class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.CUSTOM_EMAIL_ENABLED_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.CUSTOM_EMAIL_ENABLED_DESCRIPTION')
        "
      >
        <div class="flex items-center gap-2">
          <input
            id="customEmailDomainEnabled"
            v-model="customEmailDomainEnabled"
            type="checkbox"
          />
          <label for="customEmailDomainEnabled">
            {{ $t('INBOX_MGMT.EDIT.ENABLE_CUSTOM_EMAIL.LABEL') }}
          </label>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://chat.jarabot.com/hc/jarabot-napoveda/articles/1752920186-posilani-emailu-z-vlastni-domeny"
          >
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.CUSTOM_EMAIL_ENABLED_LINK_TO_DOCS')
            }}
          </a>
        </div>
      </SettingsSection>
    </div>
    <div class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.EDIT.ADDITIONAL_SIGNATURES.TITLE')"
        :sub-title="$t('INBOX_MGMT.EDIT.ADDITIONAL_SIGNATURES.SUB_TEXT')"
      >
        <div class="flex flex-col gap-4">
          <div
            v-for="(signature, index) in additionalSignatures"
            :key="index"
            class="border border-n-slate-6 rounded-md p-3"
          >
            <div class="flex justify-between items-center mb-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  $t('INBOX_MGMT.EDIT.ADDITIONAL_SIGNATURES.OPTION', {
                    number: index + 1,
                  })
                }}
              </span>
              <button
                type="button"
                class="text-red-600 hover:text-red-800 text-sm"
                @click="removeSignature(index)"
              >
                {{ $t('INBOX_MGMT.EDIT.ADDITIONAL_SIGNATURES.REMOVE') }}
              </button>
            </div>
            <HtmlSignaturePreview
              :html-signature="signature.html"
              @update="html => updateSignatureHtml(index, html)"
            />
          </div>
          <div>
            <NextButton
              ghost
              blue
              size="small"
              :label="$t('INBOX_MGMT.EDIT.ADDITIONAL_SIGNATURES.ADD')"
              @click="addSignature"
            />
          </div>
          <p class="text-sm not-italic text-n-slate-11">
            {{ $t('INBOX_MGMT.EDIT.ADDITIONAL_SIGNATURES.HELP_TEXT') }}
          </p>
        </div>
      </SettingsSection>
      <SettingsSection
        :title="$t('INBOX_MGMT.EDIT.ALTERNATIVE_EMAILS.TITLE')"
        :sub-title="$t('INBOX_MGMT.EDIT.ALTERNATIVE_EMAILS.SUB_TEXT')"
      >
        <div class="flex flex-col gap-2">
          <div
            v-for="(emailAlias, index) in emailAliases"
            :key="index"
            class="flex gap-2 items-center"
          >
            <input
              v-model="emailAlias.email"
              type="email"
              class="!mb-0 flex-1"
              :placeholder="
                $t('INBOX_MGMT.EDIT.ALTERNATIVE_EMAILS.PLACEHOLDER')
              "
            />
            <select
              v-model.number="alternativeEmailSignatures[index]"
              class="!mb-0 flex-1"
              :aria-label="
                $t('INBOX_MGMT.EDIT.ALTERNATIVE_EMAILS.SIGNATURE_LABEL')
              "
            >
              <option :value="-1">
                {{ $t('INBOX_MGMT.EDIT.ALTERNATIVE_EMAILS.PRIMARY_SIGNATURE') }}
              </option>
              <option
                v-for="(signature, sIndex) in additionalSignatures"
                :key="sIndex"
                :value="sIndex"
              >
                {{
                  $t('INBOX_MGMT.EDIT.ADDITIONAL_SIGNATURES.OPTION', {
                    number: sIndex + 1,
                  })
                }}
              </option>
            </select>
            <button
              type="button"
              class="text-red-600 hover:text-red-800 text-sm"
              @click="removeAlternativeEmail(index)"
            >
              {{ $t('INBOX_MGMT.EDIT.ALTERNATIVE_EMAILS.REMOVE') }}
            </button>
          </div>
          <div>
            <NextButton
              ghost
              blue
              size="small"
              :label="$t('INBOX_MGMT.EDIT.ALTERNATIVE_EMAILS.ADD')"
              @click="addAlternativeEmail"
            />
          </div>
          <p class="text-sm not-italic text-n-slate-11">
            {{ $t('INBOX_MGMT.EDIT.ALTERNATIVE_EMAILS.HELP_TEXT') }}
          </p>
        </div>
      </SettingsSection>
      <SettingsSection :show-border="false">
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          @click="updateConfiguration"
        />
      </SettingsSection>
    </div>
    <template v-if="!isOAuthProvider && !customEmailDomainEnabled">
      <ImapSettings ref="imapSettings" :inbox="inbox" />
      <SmtpSettings v-if="inbox.imap_enabled" :inbox="inbox" />
    </template>
  </div>
</template>
